import React, { useCallback, useState, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useQuery } from "@tanstack/react-query";
import LinearProgress from "@mui/material/LinearProgress";
import Link from "@mui/material/Link";
import { toast } from "react-toastify";
import { Task } from "../types";
import { SideOverlay } from "../../../components";
import { useStore } from "../../../helpers";
import { safe_get } from "../../../report-visuals";
import { TaskActions } from "../components/TaskActions";
import { TaskHeader } from "../components/TaskHeader";
import { ConfirmationModal } from "../components/ConfirmationModal";
import { taskQueryKeys } from "../utils/queryKeys";
import { useTaskMutations } from "../hooks/useTaskMutations";
import {
    sortSurveysByOldestFirst,
    transformSurveysToLocations,
} from "../utils/surveyUtils";
import {
    CollapsibleSection,
    LocationCoverage,
    SurveyTimeline,
    SurveyLocationMap,
    UserDisplay,
    StatisticsCard,
    TaskLogs,
} from "../components";
import { StatusCountCard } from "./StatusCountCard";
import { InfoPanel } from "./InfoPanel";
import BoothMapView from "../../task-manager/task-list-components/booth-map-view";

interface TaskViewProps {
    isOpen: boolean;
    onClose: () => void;
    task: Task | null;
    onEdit: () => void;
    onRefresh?: () => void;
}

export const TaskView: React.FC<TaskViewProps> = observer(
    ({ isOpen, onClose, task, onEdit, onRefresh }) => {
        const { taskManagerStore, projectStore, userStore } = useStore();
        const { isRoot, isClient } = userStore;

        // Collapsible sections state
        const [showSurveys, setShowSurveys] = useState(true);
        const [showLogs, setShowLogs] = useState(false);
        const [showLocationCoverage, setShowLocationCoverage] = useState(true);
        const [showMap, setShowMap] = useState(false);

        // Confirmation modals state
        const [showCloneModal, setShowCloneModal] = useState(false);
        const [showDeleteModal, setShowDeleteModal] = useState(false);

        const editTaskPermission = projectStore.getPermission(
            "edit_tasks",
            false
        );
        const canEdit = editTaskPermission || isRoot || isClient;

        // React Query: Fetch task details
        const {
            data: taskDetails,
            isLoading: isLoadingDetails,
            refetch: refetchTaskDetails,
        } = useQuery(
            taskQueryKeys.detail(task?.id ?? 0),
            async () => {
                if (!task) return null;
                taskManagerStore.set_active_task(task);
                const res = await taskManagerStore.view_task();
                if (res?.status === 200) {
                    const ml = safe_get(res.data, "more.marked_locations", []);
                    taskManagerStore.set_marked_locations(ml);
                    return res.data;
                }
                throw new Error("Failed to load task details");
            },
            {
                enabled: !!task && isOpen,
                onError: () => toast.error("Failed to load task details"),
            }
        );

        // Mutations
        const { syncTaskMutation, cloneTaskMutation, deleteTaskMutation } =
            useTaskMutations({
                taskId: task?.id,
                onRefresh,
                onClose,
                refetchTaskDetails,
            });

        // Handlers
        const handleSync = useCallback(() => {
            if (!task) return;
            syncTaskMutation.mutate(task.id);
        }, [task, syncTaskMutation]);

        const handleClone = useCallback(() => {
            if (!task) return;
            setShowCloneModal(true);
        }, [task]);

        const handleDelete = useCallback(() => {
            if (!task) return;
            setShowDeleteModal(true);
        }, [task]);

        const handleCloneConfirm = useCallback(() => {
            if (!task) return;
            cloneTaskMutation.mutate(task.id);
        }, [task, cloneTaskMutation]);

        const handleDeleteConfirm = useCallback(() => {
            if (!task) return;
            deleteTaskMutation.mutate(task.id);
        }, [task, deleteTaskMutation]);

        const handleShowMap = useCallback(async () => {
            if (!task) return;
            taskManagerStore.set_active_task(task);
            await taskManagerStore.set_task_view_modal(false);
            await taskManagerStore.update_show_booth_map(true);
            onClose();
        }, [task]);

        // Task view content calculations - MUST be before early return to maintain hook order
        const taskDetailsData = taskDetails || {};
        const surveyLogsRaw = safe_get(taskDetailsData, "status.surveys", []);
        const surveyLogs = Array.isArray(surveyLogsRaw) ? surveyLogsRaw : [];

        // Sort surveys by oldest first and store in separate variable - MUST be before early return
        const sortedSurveyLogs = useMemo(
            () => sortSurveysByOldestFirst(surveyLogs),
            [surveyLogs]
        );

        // Transform sorted surveys to location data for map
        const surveyLocations = useMemo(
            () => transformSurveysToLocations(sortedSurveyLogs),
            [sortedSurveyLogs]
        );

        // Early return AFTER all hooks - this is critical for React hooks rules
        if (!task) {
            return (
                <SideOverlay
                    show={isOpen}
                    onClose={onClose}
                    title="Task Details"
                    size="medium"
                >
                    <div className="p-6 text-sm text-slate-500">
                        No task selected.
                    </div>
                </SideOverlay>
            );
        }

        // Now we can safely use task since we've checked it exists
        const title = task?.node?.display || task?.more?.name || "Task Details";
        const fieldProgress = task.status?.progress_field_work || 0;
        const qaProgress = task.status?.progress_qa || 0;
        const validCount = safe_get(task, "status.by_qastatus.valid", 0);
        const invalidCount = safe_get(task, "status.by_qastatus.invalid", 0);
        const pendingCount = safe_get(
            task,
            "status.by_qastatus.not-checked",
            0
        );
        const totalInterviews = validCount + invalidCount + pendingCount;
        const completionRate = task.count?.count
            ? Math.round((totalInterviews / task.count.count) * 100)
            : 0;

        const locSummary = safe_get(
            taskDetailsData,
            "status.by_loc.loc_summary"
        );
        const locCoverage = safe_get(
            taskDetailsData,
            "status.by_loc.loc_coverage"
        );
        const taskLogs = safe_get(taskDetailsData, "logs", []);
        const boothMapUrl = safe_get(task, "node.booth_map", "");
        const boothInfoUrl = safe_get(task, "node.booth_info", "");
        const geoJsonUrl = boothMapUrl.replace(
            "https://demo.rajneethi.com/gj.html?path=",
            ""
        );
        console.log(geoJsonUrl);

        return (
            <SideOverlay
                show={isOpen}
                onClose={onClose}
                title={title}
                size="medium"
            >
                <div className="flex flex-col h-full">
                    <TaskHeader task={task} />
                    <TaskActions
                        canEdit={canEdit}
                        onEdit={onEdit}
                        onSync={handleSync}
                        onClone={handleClone}
                        onDelete={handleDelete}
                        onShowMap={handleShowMap}
                    />

                    <div className="flex-1 overflow-y-auto bg-white">
                        {isLoadingDetails && (
                            <div className="px-6 pt-2">
                                <LinearProgress />
                            </div>
                        )}
                        <div className="px-6 py-4 space-y-4">
                            {/* Task Header */}
                            <div>
                                <h3 className="text-xl font-semibold text-slate-900 mb-1">
                                    {task.node?.display ||
                                        task.more?.name ||
                                        "Untitled Task"}
                                </h3>
                                <div className="flex items-center gap-4 text-sm text-slate-500">
                                    <span>
                                        {task.node?.piv_level1}
                                        {task.node?.piv_level2 &&
                                            ` / ${task.node.piv_level2}`}
                                    </span>
                                    {boothInfoUrl && (
                                        <>
                                            <span>•</span>
                                            <Link
                                                href={boothInfoUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-indigo-600 hover:text-indigo-700"
                                            >
                                                Details
                                            </Link>
                                        </>
                                    )}
                                    {boothMapUrl && (
                                        <>
                                            <span>•</span>
                                            <Link
                                                href={boothMapUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-indigo-600 hover:text-indigo-700"
                                            >
                                                Map
                                            </Link>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Enhanced Stats Grid */}
                            <div className="grid grid-cols-2 gap-3">
                                {/* Assigned User */}
                                <InfoPanel title="Assigned To">
                                    {task.user ? (
                                        <UserDisplay
                                            user={task.user}
                                            avatarSize="sm"
                                            nameClassName="text-slate-900 font-semibold"
                                            phoneClassName="text-slate-500"
                                        />
                                    ) : (
                                        <span className="text-sm text-slate-500">
                                            Unassigned
                                        </span>
                                    )}
                                </InfoPanel>

                                {/* Interviews */}
                                <StatisticsCard
                                    label="Interviews"
                                    value={totalInterviews}
                                    subtitle={task.count?.count || 0}
                                    percentage={completionRate}
                                />
                                {/* Field Work */}
                                <InfoPanel
                                    title="Field Work"
                                    right={
                                        <span className="text-xs font-semibold text-indigo-700">
                                            {fieldProgress}%
                                        </span>
                                    }
                                >
                                    <div className="w-full bg-slate-200 rounded-full h-1.5">
                                        <div
                                            className="bg-indigo-600 h-1.5 rounded-full transition-all"
                                            style={{
                                                width: `${fieldProgress}%`,
                                            }}
                                        />
                                    </div>
                                </InfoPanel>

                                {/* QC Check */}
                                <InfoPanel
                                    title="QC Check"
                                    right={
                                        <span className="text-xs font-semibold text-amber-700">
                                            {qaProgress}%
                                        </span>
                                    }
                                >
                                    <div className="w-full bg-slate-200 rounded-full h-1.5">
                                        <div
                                            className="bg-amber-600 h-1.5 rounded-full transition-all"
                                            style={{ width: `${qaProgress}%` }}
                                        />
                                    </div>
                                </InfoPanel>

                                {/* Location Coverage */}
                                {locSummary && (
                                    <InfoPanel
                                        title="Locations"
                                        right={
                                            <span className="text-xs font-semibold text-purple-700">
                                                {locSummary["percent"]}%
                                            </span>
                                        }
                                    >
                                        <div className="w-full bg-slate-200 rounded-full h-1.5">
                                            <div
                                                className="bg-purple-600 h-1.5 rounded-full transition-all"
                                                style={{
                                                    width: `${locSummary["percent"]}%`,
                                                }}
                                            />
                                        </div>
                                        <p className="text-xs text-slate-500 mt-1">
                                            {locSummary["done"]} /{" "}
                                            {locSummary["ref"]}
                                        </p>
                                    </InfoPanel>
                                )}
                            </div>

                            {/* Interview Status */}
                            <InfoPanel
                                title="Interview Status"
                                right={
                                    <span className="text-xs text-slate-500">
                                        {totalInterviews} of{" "}
                                        {task.count?.count || 0}
                                    </span>
                                }
                            >
                                <div className="grid grid-cols-3 gap-2">
                                    <StatusCountCard
                                        label="Valid"
                                        value={validCount}
                                        className="bg-emerald-50 border-emerald-200/50"
                                        valueClassName="text-emerald-700"
                                        labelClassName="text-emerald-600"
                                    />
                                    <StatusCountCard
                                        label="Invalid"
                                        value={invalidCount}
                                        className="bg-red-50 border-red-200/50"
                                        valueClassName="text-red-700"
                                        labelClassName="text-red-600"
                                    />
                                    <StatusCountCard
                                        label="Pending"
                                        value={pendingCount}
                                        className="bg-amber-50 border-amber-200/50"
                                        valueClassName="text-amber-700"
                                        labelClassName="text-amber-600"
                                    />
                                </div>
                            </InfoPanel>

                            {/* Surveys Section */}
                            {sortedSurveyLogs &&
                                sortedSurveyLogs.length > 0 && (
                                    <CollapsibleSection
                                        title="Survey History"
                                        isOpen={showSurveys}
                                        onToggle={() =>
                                            setShowSurveys(!showSurveys)
                                        }
                                        count={sortedSurveyLogs.length}
                                        icon="fa-clipboard-check"
                                    >
                                        <SurveyTimeline
                                            surveys={sortedSurveyLogs}
                                        />
                                    </CollapsibleSection>
                                )}

                            {/* Survey Map Section */}
                            {surveyLocations && surveyLocations.length > 0 && (
                                <CollapsibleSection
                                    title="Survey Locations Map"
                                    isOpen={showMap}
                                    onToggle={() => setShowMap(!showMap)}
                                    count={surveyLocations.length}
                                    icon="fa-map"
                                >
                                    <SurveyLocationMap
                                        locations={surveyLocations}
                                        height={400}
                                    />
                                </CollapsibleSection>
                            )}

                            {/* Task Logs Section */}
                            {taskLogs && taskLogs.length > 0 && (
                                <CollapsibleSection
                                    title="Activity Log"
                                    isOpen={showLogs}
                                    onToggle={() => setShowLogs(!showLogs)}
                                    count={taskLogs.length}
                                    icon="fa-clock-rotate-left"
                                >
                                    <TaskLogs logs={taskLogs} />
                                </CollapsibleSection>
                            )}

                            {/* Location Coverage Section */}
                            {locCoverage &&
                                Object.keys(locCoverage).length > 0 && (
                                    <CollapsibleSection
                                        title="Location Coverage"
                                        isOpen={showLocationCoverage}
                                        onToggle={() =>
                                            setShowLocationCoverage(
                                                !showLocationCoverage
                                            )
                                        }
                                        count={Object.keys(locCoverage).length}
                                        icon="fa-map-location-dot"
                                    >
                                        <LocationCoverage data={locCoverage} />
                                    </CollapsibleSection>
                                )}
                        </div>
                    </div>
                    {/* <BoothMapView geoJsonUrl={geoJsonUrl} task={task} /> */}
                </div>
                <ConfirmationModal
                    isOpen={showCloneModal}
                    onClose={() => setShowCloneModal(false)}
                    onConfirm={handleCloneConfirm}
                    title="Clone Task"
                    message={`Are you sure you want to clone "${task?.node?.display || task?.more?.name || "this task"}"? A new task will be created with the same settings.`}
                    confirmText="Clone"
                    cancelText="Cancel"
                    confirmButtonColor="primary"
                />
                <ConfirmationModal
                    isOpen={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={handleDeleteConfirm}
                    title="Delete Task"
                    message={`Are you sure you want to delete "${task?.node?.display || task?.more?.name || "this task"}"? This action cannot be undone.`}
                    confirmText="Delete"
                    cancelText="Cancel"
                    confirmButtonColor="error"
                />
            </SideOverlay>
        );
    }
);
